/**
 * Poll For Idle Actor
 *
 * XState actor that polls the status endpoint until the process completes.
 * Uses the callback pattern for continuous polling with cleanup support.
 */
import { fromCallback } from 'xstate'
import { fetchImportPreviewStatus } from '../../importer-api'

export interface PollForIdleInput {
  importNumber: number
}

export type PollForIdleEvent =
  | { type: 'POLL_COMPLETE' }
  | { type: 'POLL_ERROR'; error: string }

const POLL_INTERVAL = 2000 // 2 seconds

export const pollForIdleActor = fromCallback<
  PollForIdleEvent,
  PollForIdleInput
>(({ input, sendBack }) => {
  let isActive = true
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const poll = async () => {
    if (!isActive) return

    try {
      const response = await fetchImportPreviewStatus(input.importNumber)

      if (!isActive) return

      if (response.status === 'idle') {
        sendBack({ type: 'POLL_COMPLETE' })
        return // Stop polling
      }

      // Still running, poll again after interval
      if (isActive) {
        timeoutId = setTimeout(poll, POLL_INTERVAL)
      }
    } catch (error) {
      if (!isActive) return
      sendBack({
        type: 'POLL_ERROR',
        error: error instanceof Error ? error.message : 'Polling failed',
      })
    }
  }

  // Start polling immediately
  poll()

  // Cleanup function - called when actor is stopped
  return () => {
    isActive = false
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
})
